import { wallOpeningOpening3 } from "building-common/openings/openingQuery";
import { tagBuildingFaces } from "building-types/buildingGeomTag/buildingGeomTags";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { PlanarGraphPerimeterInfo } from "building-types/planarGraphs/planarGraphPerimeterInfo";
import type { GeneratedRoof, GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallSettings } from "building-types/settings/wallSettings";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import { type SolidWall, type SolidWallMass } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import type { Mutable } from "common/utils/types";
import { roofSystemsOnLevel } from "doc/building/roofSystemsOnLevel";
import { windowPlacementsOnLevel } from "doc/building/windowPlacementsOnLevel";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { MULTIGABLE_BSP_EPSILON, OPENING_TRANSFER_DISTANCE_TOLERANCE } from "doc/utils/roofTools";
import { aabb2AddPoint, aabb2ByTwoPoints, aabb2Overlaps } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { aabb3AddPoint, aabb3Init, aabb3Overlaps, aabb3Union } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import type { Face } from "geom/brep";
import {
  faceAABB3,
  faceOrNullCopy,
  faceOrNullMoveAlongNormal,
  faceNew,
  faceOrNullReverse,
  faceIsDefined,
} from "geom/brep";
import { tagFaces } from "geom/brep";
import { addPolygonsToBSP, buildPolygonsFromFaces, isPointInsideBSP, polygonBSPNew, splitPolygonBSP } from "geom/bsp";
import type { ClipFaceContext, SplitFaceBSPOptions } from "geom/bspClipping";
import { clipFace, clipFaces, clippingNodeNew, splitFaceBSP, splitFacesBSP } from "geom/bspClipping";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { ANGLE_EPSILON, DIST_EPSILON } from "geom/core";
import { extrudeLine2 } from "geom/extrude";
import { CurveCurveRangeIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { line2ProjectPt, line2Tangent } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import {
  planeByThreePoints,
  planeIsCoplanar,
  planeMoveAlongNormal,
  planeNewXY,
  planeNormal,
  planeProjectPolyLine3,
  planeSignedDistanceTo,
  polyLine2OnPlane,
} from "geom/plane";
import { planeBSPByThreePoints } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polygon";
import {
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Length,
  polyLine2SetCW,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import {
  polyLine3Close,
  polyLine3CopyReversed,
  polyLine3EdgeCount,
  polyLine3EdgeStart,
  polyLine3New,
  polyLine3OnPlane,
} from "geom/polyLine3";
import { vec2Dot } from "geom/vec2";
import { vec3Dot } from "geom/vec3";
import { bottomLevelId } from "../level/levelTools";
import { adjustFacesForZFighting } from "../wallAssemblies/wallFinishFaces";
import { windowRectangularOpening } from "../window/windowElevation";
import { SolidWallChangeSet } from "./solidWallChangeSet";
import { getWallPath, wallHasNonInteriorRoomEnclosure } from "./wall";
import { wallsCrossSectionFaces } from "./wallCrossSectionFaces";
import { makeWallCutHalfEdgeMaps, performWallCut3 } from "./wallCut";
import { solidWallSubstrateLayer } from "./wallLayers";

export function trimCeilingFaceWithHalfStoryRoofs({ face, roofs }: { face: Face; roofs: Table<GeneratedRoofSystem> }) {
  let insideCount = 0;
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    if (trimSimpleFaceWithBSP(face, roof.clipContext.insideBSP)) {
      insideCount++;
    }
  }

  return insideCount > 0 || Object.keys(roofs).length === 0;
}

interface SelectWalls {
  roofSystems: Table<GeneratedRoofSystem>;
  rooms: Table<Room>;
  blockIsHalfStory: { [blockId: string]: boolean };
  levels: Table<Level>;
  mirror: boolean;
  wallSettings: WallSettings;
  doorPlacements: Table<GeneratedDoorPlacement>;
  windowPlacements: Table<GeneratedWindowPlacement>;
  planarGraphPerimeters: { [levelId: string]: PlanarGraphPerimeterInfo };
  levelIsHalfStory: { [levelId: string]: boolean };
}

export function filterHalfStoryWindows(
  levelId: string,
  wallMasses: WallMassSystem,
  {
    roofSystems: roofs,
    rooms,
    blockIsHalfStory,
    levels,
    windowPlacements,
  }: SelectWalls & { windowPlacements: RWTable<GeneratedWindowPlacement> },
) {
  const levelWindows = windowPlacementsOnLevel(levelId, windowPlacements);
  const [halfEdgeToWindowSet] = makeWallCutHalfEdgeMaps([], levelWindows, []);

  const isExteriorWall: { [key: string]: boolean } = {};

  const roofsBelowOrOnLevel = halfStoryRoofs(levels, levelId, roofs);

  const isWindowBelowRoofs = (
    window: GeneratedWindowPlacement,
    wall: SolidWallMass,
    isLeftWindow: boolean,
  ): boolean => {
    if (!window) {
      return false;
    }

    const { leftEdge, rightEdge, leftFace, rightFace } = wall;
    const frontFace = isLeftWindow ? leftFace : rightFace;
    const frontEdge = isLeftWindow ? leftEdge : rightEdge;

    const frontHole = windowRectangularOpening(window, frontEdge, polyLine2Length(frontEdge), wallMasses);
    if (!frontHole) {
      return false;
    }

    const { plane: frontPlane } = frontFace;

    const frontOutline3 = polyLine3OnPlane(frontHole.opening2, frontPlane);

    roofIteration: for (const roof of roofsBelowOrOnLevel) {
      const { insideBSP } = roof.clipContext;
      const outlineCount = frontOutline3 ? polyLine3EdgeCount(frontOutline3) : 0;

      for (let i = 0; i < outlineCount; ++i) {
        const [x, y, z] = polyLine3EdgeStart(frontOutline3, i);
        if (!isPointInsideBSP(insideBSP, x, y, z, false, MULTIGABLE_BSP_EPSILON)) {
          continue roofIteration;
        }
      }
      return true;
    }

    return false;
  };

  for (const roof of roofsBelowOrOnLevel) {
    // only working for multi-gables atm

    for (const id in wallMasses.walls) {
      const wall = wallMasses.walls[id];

      if (wall.wallType !== WallType.SOLID_MASS || !solidWallIsPartOfHalfStory(wall, blockIsHalfStory)) {
        continue;
      }

      if (solidWallIsExteriorWall(wall, roof.roofs)) {
        isExteriorWall[id] = true;
      }
    }
  }

  for (const id in wallMasses.walls) {
    const wall = wallMasses.walls[id];

    if (wall.wallType !== WallType.SOLID_MASS) {
      continue;
    }

    const path = getWallPath(wall);
    const { leftRoom: leftRoomLike, rightRoom: rightRoomLike } = path;
    const halfEdge = path.halfEdgeIndex;
    const oppHalfEdge = planarGraphAdjacentHalfEdge(path.halfEdgeIndex);

    const leftRoom = leftRoomLike ? rooms[leftRoomLike.id] : null;
    const rightRoom = rightRoomLike ? rooms[rightRoomLike.id] : null;

    const isLeftHalfStory = leftRoom ? blockIsHalfStory[leftRoom.parentId] : true;
    const isRightHalfStory = rightRoom ? blockIsHalfStory[rightRoom.parentId] : true;

    if (isLeftHalfStory && isRightHalfStory) {
      const leftWindows = halfEdgeToWindowSet[halfEdge];
      const rightWindows = halfEdgeToWindowSet[oppHalfEdge];

      if (leftWindows) {
        for (const window of leftWindows) {
          if (!isExteriorWall[id] || !isWindowBelowRoofs(window, wall, true)) {
            delete windowPlacements[window.id];
          }
        }
      }

      if (rightWindows) {
        for (const window of rightWindows) {
          if (!isExteriorWall[id] || !isWindowBelowRoofs(window, wall, false)) {
            delete windowPlacements[window.id];
          }
        }
      }
    }
  }
}

function halfStoryRoofs(
  levels: Table<Level>,
  levelId: string,
  roofs: Table<GeneratedRoofSystem>,
): GeneratedRoofSystem[] {
  const levelBelow = levels[levelId].levelBelow ?? bottomLevelId(levels);
  const roofsBelow = levelBelow ? roofSystemsOnLevel(levelBelow, roofs) : [];
  const roofsBelowOrOnLevel = roofsBelow.length > 0 ? roofsBelow : roofSystemsOnLevel(levelId, roofs);
  return roofsBelowOrOnLevel;
}

export function trimWallSystemWithHalfStoryRoofs(
  levelId: string,
  levelWalls: Mutable<WallSystem>,
  {
    levels,
    doorPlacements: doors,
    windowPlacements: windows,
    roofSystems: roofs,
    blockIsHalfStory,
    rooms,
    wallSettings: wallConfig,
    planarGraphPerimeters,
    levelIsHalfStory,
    mirror,
  }: SelectWalls,
) {
  const halfStoryWallCrossSectionFaces: Face[] = [];

  const level = levels[levelId];
  const planarGraphPerimeter = planarGraphPerimeters[levelId];
  const planarGraph = levelWalls.wallIndex.planarGraph;
  const exteriorWallIds: Set<string> = new Set([]);
  const substrateThickness = wallConfig.exteriorWallSubstrateThickness;
  const clipHeight = level.floorElevation + level.topOfWall - 1;
  const wallCrossSectionFaces = wallsCrossSectionFaces(
    planarGraph.planarGraph,
    levelWalls,
    clipHeight,
    WallLayerType.SUBSTRATE,
    true,
    false,
  );

  const windowsToRemoveByWall: { [wallId: string]: { [windowID: string]: number } } = {};
  const addWindowToRemoveList = (wallId: string, windowId: string) => {
    if (!windowsToRemoveByWall[wallId]) {
      windowsToRemoveByWall[wallId] = {};
    }

    const windowsToRemove = windowsToRemoveByWall[wallId];
    windowsToRemove[windowId] = (windowsToRemove[windowId] ?? 0) + 1;
  };

  // find the bounding box(es) of the wall system on this level

  let levelBB: AABB2 | undefined;
  const wallAABB3s: { [id: string]: AABB3 } = {};

  for (const id in levelWalls.walls) {
    const wall = levelWalls.walls[id];
    const path = getWallPath(wall);

    const aabb3 = aabb3Init();
    aabb3AddPoint(aabb3, path.startX, path.startY, level.floorElevation);
    aabb3AddPoint(aabb3, path.endX, path.endY, level.floorElevation);
    aabb3AddPoint(aabb3, path.startX, path.startY, level.floorElevation + level.topOfWall);
    aabb3AddPoint(aabb3, path.endX, path.endY, level.floorElevation + level.topOfWall);
    wallAABB3s[id] = aabb3;

    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    if (!levelBB) {
      levelBB = aabb2ByTwoPoints(path.startX, path.startY, path.endX, path.endY);
      continue;
    }

    aabb2AddPoint(levelBB, path.startX, path.startY);
    aabb2AddPoint(levelBB, path.endX, path.endY);
  }

  const roofAABB3s: { [id: string]: AABB3 } = {};

  const roofsNearLevel = halfStoryRoofs(levels, levelId, roofs);
  for (const roof of roofsNearLevel) {
    const { roofs: subRoofs } = roof;

    if (!levelBB) {
      continue;
    }

    // clip the wall system on this level with those roofs
    const { insideBSP } = roof.clipContext;

    // clipped wall cross section faces
    clipFaces({
      faces: wallCrossSectionFaces,
      options: {
        clippingNodes: [clippingNodeNew(insideBSP, true)],
      },
      clippedFaces: halfStoryWallCrossSectionFaces,
    });

    // TODO: a tempting optimization is to keep an ever reducing `wallCrossSectionFaces` array.
    //       The problem is if there are multiple roof systems, each keeps only the poche faces
    //       within its volume (insideBSP). If there are poche faces that are not within any roof
    //       system, they will be lost. Even with this optimization commented out, it is possible
    //       to create levels with half story blocks and two roof systems that don't fully cover
    //       every poche face. In that case, those poche faces will be lost. With the current solution
    //       those faces end up duplicated :(
    //       The correct solution would involve unifying the volumes of the roof systems AND the rooms
    //       above the half story blocks and do a single clip operation on the poche faces using that.
    //       This is a non-trivial task and would require a lot of changes.
    //    wallCrossSectionFaces = halfStoryWallCrossSectionFaces;

    if (!levelIsHalfStory[levelId]) {
      continue;
    }

    // find the sub-roofs that are inside or outside of that bounding box and collect their faces
    // for clipping

    const clipSubRoofs: RWTable<GeneratedRoof> = {};

    const tryAddFace = (face) => {
      const bb = faceAABB3(face);
      if (aabb2Overlaps(levelBB, bb)) {
        return true;
      }
      return false;
    };

    let roofAABB3 = aabb3Init();

    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];

      let subRoofAdded = false;

      const { leftFaces, rightFaces, startHipFaces, endHipFaces } = subRoof.roofVolume.extrusion;
      for (const face of leftFaces) {
        subRoofAdded = tryAddFace(face) || subRoofAdded;
      }

      for (const face of rightFaces) {
        subRoofAdded = tryAddFace(face) || subRoofAdded;
      }

      for (const face of startHipFaces) {
        subRoofAdded = tryAddFace(face) || subRoofAdded;
      }

      for (const face of endHipFaces) {
        subRoofAdded = tryAddFace(face) || subRoofAdded;
      }

      if (subRoofAdded) {
        roofAABB3 = aabb3Union(roofAABB3, subRoof.roofVolume.aabb3);
        clipSubRoofs[subRoofId] = subRoof;
      }
    }
    roofAABB3s[roof.id] = roofAABB3;

    for (const id in levelWalls.walls) {
      if (!aabb3Overlaps(wallAABB3s[id], roofAABB3)) {
        continue;
      }
      let wall = levelWalls.walls[id];

      // ignore void walls
      if (wall.wallType !== WallType.SOLID) {
        continue;
      }

      const { mass, leftSide, rightSide } = wall;
      const path = getWallPath(wall);
      const { leftFace, rightFace } = mass;
      const { leftRoom: leftRoomLike, rightRoom: rightRoomLike } = path;

      const leftRoom = leftRoomLike ? rooms[leftRoomLike.id] : null;
      const rightRoom = rightRoomLike ? rooms[rightRoomLike.id] : null;

      const isLeftHalfStory = leftRoom ? blockIsHalfStory[leftRoom.parentId] : true;
      const isRightHalfStory = rightRoom ? blockIsHalfStory[rightRoom.parentId] : true;

      let openingCount = 0;

      openingCount += leftSide.windows.length;
      openingCount += rightSide.windows.length;

      if (isLeftHalfStory && isRightHalfStory) {
        const isExteriorEndWall = solidWallIsExteriorWall(wall, clipSubRoofs);
        const changeSet = new SolidWallChangeSet(wall, levelId, levelWalls, planarGraphPerimeter, mirror);

        if (isExteriorEndWall) {
          exteriorWallIds.add(wall.id);
          continue;
        }

        for (const window of leftSide.windows) {
          if (!window?.opening?.front?.opening2) {
            continue;
          }
          const faceLoop = polyLine2SetCW(polyLine2Copy(window?.opening?.front?.opening2));
          const openingFace = faceNew([faceLoop], leftFace.plane);
          const outsideFaces = clipFace({
            face: openingFace,
            options: {
              clippingNodes: [clippingNodeNew(insideBSP)],
              coplanarsToFront: false,
              epsilon: MULTIGABLE_BSP_EPSILON,
            },
          });

          if (outsideFaces.length > 0) {
            changeSet.removeWindow(window);
            addWindowToRemoveList(id, window.id);
            openingCount--;
          }
        }

        for (const window of rightSide.windows) {
          if (!window?.opening?.front?.opening2) {
            continue;
          }

          const faceLoop = polyLine2SetCW(polyLine2Copy(window.opening.front.opening2));
          const openingFace = faceNew([faceLoop], rightFace.plane);
          const outsideFaces = clipFace({
            face: openingFace,
            options: {
              clippingNodes: [clippingNodeNew(insideBSP)],
              coplanarsToFront: false,
              epsilon: MULTIGABLE_BSP_EPSILON,
            },
          });

          if (outsideFaces.length > 0) {
            changeSet.removeWindow(window);
            addWindowToRemoveList(id, window.id);
            openingCount--;
          }
        }

        wall = changeSet.apply(doors, windows);
      }

      // imprint windows from clipped wall onto end gable walls
      if (openingCount > 0) {
        for (const subRoofId in clipSubRoofs) {
          const {
            roofVolume: { startFaces, endFaces },
            wallFaces: { startWallFaces, endWallFaces },
          } = clipSubRoofs[subRoofId];

          const { face: startFrontFace } = startFaces;
          const { face: endFrontFace } = endFaces;

          if (endFrontFace && planeIsCoplanar(endFrontFace.plane, leftFace.plane)) {
            gableWallTransferWindowsFromSolidWall(endFrontFace.plane, endWallFaces, wall, true);
          } else if (endFrontFace && planeIsCoplanar(endFrontFace.plane, rightFace.plane)) {
            gableWallTransferWindowsFromSolidWall(endFrontFace.plane, endWallFaces, wall, false);
          } else if (startFrontFace && planeIsCoplanar(startFrontFace.plane, leftFace.plane)) {
            gableWallTransferWindowsFromSolidWall(startFrontFace.plane, startWallFaces, wall, true);
          } else if (startFrontFace && planeIsCoplanar(startFrontFace.plane, rightFace.plane)) {
            gableWallTransferWindowsFromSolidWall(startFrontFace.plane, startWallFaces, wall, false);
          }
        }
      }
    }
  }

  // remove unused windows
  for (const wallId in windowsToRemoveByWall) {
    const windowsToRemove = windowsToRemoveByWall[wallId];
    const wall = levelWalls.walls[wallId];

    // ignore void walls
    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    const changeSet = new SolidWallChangeSet(wall, levelId, levelWalls, planarGraphPerimeter, mirror);

    for (const windowsId in windowsToRemove) {
      if (windowsToRemove[windowsId] === roofsNearLevel.length) {
        changeSet.removeWindow(windows[windowsId]);
      }
    }

    levelWalls.walls[wallId] = changeSet.apply(doors, windows);
  }

  // clip walls
  const wallsToAdjust: { string?: boolean } = {};

  for (const roof of roofsNearLevel) {
    // only working for multi-gables atm

    const { clipContext } = roof;
    const { insideBSP } = clipContext;

    for (const id in levelWalls.walls) {
      if (!aabb3Overlaps(wallAABB3s[id], roofAABB3s[roof.id])) {
        continue;
      }

      const wall = levelWalls.walls[id];

      /**
       * @TODO - void walls can have solid wall sections, but they are not clipped by the
       * roof system due to this check and the inability of the VOID walls to store WallFaces.
       * This needs to be updated
       */
      // ignore void walls
      if (wall.wallType !== WallType.SOLID) {
        continue;
      }

      const tag: BuildingGeomTag[] = [
        {
          type: "PARENT",
          parentId: levelId,
          parentType: "LEVEL",
        },
        {
          type: "OPERATION",
          operation: "TRIM_WALL_SYSTEM_WITH_HALF_STORY_ROOFS",
        },
      ];

      const { wallFaces } = wall;
      const path = getWallPath(wall);
      const { leftEdge, rightEdge, thickness } = solidWallSubstrateLayer(wall);
      const { leftRoom: leftRoomLike, rightRoom: rightRoomLike } = path;

      const leftRoom = leftRoomLike ? rooms[leftRoomLike.id] : null;
      const rightRoom = rightRoomLike ? rooms[rightRoomLike.id] : null;

      const isLeftHalfStoryOrUndefined = leftRoom ? blockIsHalfStory[leftRoom.parentId] : true;
      const isRightHalfStoryOrUndefined = rightRoom ? blockIsHalfStory[rightRoom.parentId] : true;

      const isLeftRoomInterior = leftRoom?.attributes
        ? isInteriorRoomEnclosure[leftRoom?.attributes.roomEnclosure]
        : false;
      const isRightRoomInterior = rightRoom?.attributes
        ? isInteriorRoomEnclosure[rightRoom?.attributes.roomEnclosure]
        : false;

      // if both rooms are half-stories or this is a half-story boundary
      if (isLeftHalfStoryOrUndefined && isRightHalfStoryOrUndefined) {
        // clip the walls
        if (levels[levelId].floorIndex > 0) {
          const exteriorOptions: ClipFaceContext = {
            clippingNodes: [clippingNodeNew(insideBSP)],
            coplanarsToFront: true,
            epsilon: MULTIGABLE_BSP_EPSILON,
          };

          if (!leftRoom) {
            wallFaces.leftFaces = tagBuildingFaces(
              clipFaces({
                faces: wallFaces.leftFaces,
                options: exteriorOptions,
              }).filter(faceIsDefined),
              tag,
            );
          }

          if (!rightRoom) {
            wallFaces.rightFaces = tagBuildingFaces(
              clipFaces({
                faces: wallFaces.rightFaces,
                options: exteriorOptions,
              }).filter(faceIsDefined),
              tag,
            );
          }
        }

        if (level.floorIndex > 0) {
          wallsToAdjust[id] = true;
        }
      } else if (isLeftHalfStoryOrUndefined && isLeftRoomInterior) {
        // if only left side is a half-story and interior,
        // otherwise it's already exterior

        if (!rightRoom) {
          // split the left face into exterior and interior faces
          const interiorFaces: Face[] = [];
          const splitOptions: SplitFaceBSPOptions = {
            node: insideBSP,
            coplanarsToFront: true,
            frontFaces: wallFaces.leftExteriorFaces,
            backFaces: interiorFaces,
            epsilon: MULTIGABLE_BSP_EPSILON,
          };

          splitFacesBSP(wallFaces.leftFaces, splitOptions);

          wallFaces.leftFaces = tagFaces(interiorFaces, tag);
        }

        // generate the caps
        wallFaces.leftExteriorFaces.push(
          ...tagFaces(makeClippedWallCapFaces(thickness, substrateThickness, leftEdge, insideBSP, level), tag),
        );
      } else if (isRightHalfStoryOrUndefined && isRightRoomInterior) {
        // if only right side is a half-story and interior,
        // otherwise it's already exterior

        if (!leftRoom) {
          // split the right face into exterior and interior faces
          const interiorFaces: Face[] = [];
          const splitOptions: SplitFaceBSPOptions = {
            node: insideBSP,
            coplanarsToFront: true,
            frontFaces: wallFaces.rightExteriorFaces,
            backFaces: interiorFaces,
            epsilon: MULTIGABLE_BSP_EPSILON,
          };

          splitFacesBSP(wallFaces.rightFaces, splitOptions);

          wallFaces.rightFaces = tagFaces(interiorFaces, tag);
        }

        // generate the caps
        wallFaces.rightExteriorFaces.push(
          ...tagFaces(makeClippedWallCapFaces(thickness, substrateThickness, rightEdge, insideBSP, level), tag),
        );
      }
    }
  }

  for (const id in wallsToAdjust) {
    const wall = levelWalls.walls[id];

    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    const { wallFaces } = wall;

    /**
     * @NOTE - this hardCoded value is almost certainly not correct in some instances,
     * and zFighting is being deprecated.
     * @TODO - remove this adjustment after face tagging
     */
    adjustFacesForZFighting(wallFaces.leftFaces, -0.0125);
    adjustFacesForZFighting(wallFaces.rightFaces, -0.0125);
  }

  const perimeter = planarGraphPerimeters[levelId].coveredPerimeters;

  // Level 0 and below don't interact with the roof system, so we manually add all exterior walls
  if (level.floorIndex <= 0) {
    for (const halfEdgeLoop of perimeter.faceHalfEdges) {
      for (const halfEdge of halfEdgeLoop) {
        exteriorWallIds.add(levelWalls.halfEdgeToWall[halfEdge]);
      }
    }
  }

  // Determine walls adjacent to exterior walls
  const adjacentToExteriorWallIds = new Set([] as string[]);
  for (const halfEdgeLoop of perimeter.faceHalfEdges) {
    let previousHalfEdge = halfEdgeLoop[halfEdgeLoop.length - 1];
    for (const halfEdge of halfEdgeLoop) {
      const wallId = levelWalls.halfEdgeToWall[halfEdge];
      const wall = levelWalls.walls[wallId];
      const prevWallId = levelWalls.halfEdgeToWall[previousHalfEdge];
      const prevWall = levelWalls.walls[prevWallId];

      if (wall.wallType === WallType.SOLID && exteriorWallIds.has(wallId) && !exteriorWallIds.has(prevWallId)) {
        adjacentToExteriorWallIds.add(prevWallId);
      } else if (
        prevWall.wallType === WallType.SOLID &&
        exteriorWallIds.has(prevWallId) &&
        !exteriorWallIds.has(wallId)
      ) {
        adjacentToExteriorWallIds.add(wallId);
      }
      previousHalfEdge = halfEdge;
    }
  }

  levelWalls.halfStoryWallCrossSectionFaces = halfStoryWallCrossSectionFaces;
}

export function makeClippedWallCapFaces(
  thickness: number,
  substrateThickness: number,
  wallEdge: PolyLine2,
  insideBSP: NodeBSP | null,
  level: Level,
): Face[] {
  const capFaces: Face[] = [];
  const offset1 = 2 * MULTIGABLE_BSP_EPSILON;

  const checkElevation = level.floorElevation + level.topOfWall - offset1;

  const splitOptions: SplitFaceBSPOptions = {
    node: insideBSP,
    coplanarsToFront: true,
    frontFaces: capFaces,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const tryToAddCapFace = (isStart: boolean) => {
    const [x, y] = isStart ? polyLine2Start(wallEdge) : polyLine2End(wallEdge);
    const [tX, tY] = polyLine2EdgeTangent(wallEdge, 0, isStart);

    // wall path point is still inside the BSP
    if (isPointInsideBSP(insideBSP, x, y, checkElevation, true, MULTIGABLE_BSP_EPSILON)) {
      // create the extra face base line
      let [x0, y0] = isStart ? polyLine2Start(wallEdge) : polyLine2End(wallEdge);
      x0 -= tX * substrateThickness;
      y0 -= tY * substrateThickness;

      let x1 = x0 + tX * (thickness + 2 * substrateThickness);
      let y1 = y0 + tY * (thickness + 2 * substrateThickness);

      // fix order
      if (isStart) {
        let tmp = x0;
        x0 = x1;
        x1 = tmp;

        tmp = y0;
        y0 = y1;
        y1 = tmp;
      }

      // generate cap face
      const capFace = extrudeLine2(planeNewXY(0, 0, level.floorElevation), x0, y0, x1, y1, 0, 0, level.topOfWall);
      if (!capFace) {
        return;
      }

      faceOrNullMoveAlongNormal(capFace, substrateThickness);

      // clip face
      splitFaceBSP(capFace, splitOptions);
    }
  };

  // generate cap faces
  tryToAddCapFace(true);
  tryToAddCapFace(false);

  // clip cap faces
  const options: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(insideBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  return clipFaces({
    faces: capFaces,
    options,
  });
}

export function gableWallTransferWindowsToSolidWall(
  gablePlane: Plane,
  windows: Table<GeneratedWindowPlacement>,
  wall: SolidWall,
  isRightSide: boolean,
) {
  const { mass } = wall;
  const { leftFace, rightFace } = mass;
  const refPlane = isRightSide ? rightFace.plane : leftFace.plane;

  for (const windowId in windows) {
    const { opening } = windows[windowId];
    if (!opening) {
      continue;
    }

    const frontOpening3 = wallOpeningOpening3(opening, "FRONT", false);

    const [n1X, n1Y, n1Z] = planeNormal(gablePlane);
    const [n2X, n2Y, n2Z] = planeNormal(refPlane);
    const revert = vec3Dot(n1X, n1Y, n1Z, n2X, n2Y, n2Z) > 0;

    if (!frontOpening3) {
      continue;
    }
    const hole3 = revert ? polyLine3CopyReversed(frontOpening3) : frontOpening3;

    performWallCut3(wall.wallFaces, gablePlane, hole3);
  }
}

function gableWallTransferWindowsFromSolidWall(
  gablePlane: Plane,
  wallFaces: WallFaces,
  wall: SolidWall,
  left: boolean,
) {
  const { leftSide, rightSide } = wall;
  for (const { opening } of leftSide.windows) {
    if (!opening) {
      continue;
    }

    // get the front opening from the left window
    const hole3 = wallOpeningOpening3(opening, left ? "FRONT" : "BACK", false);
    if (!hole3) {
      continue;
    }
    performWallCut3(wallFaces, gablePlane, hole3);
  }

  for (const { opening } of rightSide.windows) {
    if (!opening) {
      continue;
    }

    // get the front opening from the left window
    const hole3 = wallOpeningOpening3(opening, left ? "FRONT" : "BACK", false);
    if (!hole3) {
      continue;
    }
    performWallCut3(wallFaces, gablePlane, hole3);
  }
}

function solidWallIsExteriorWall(wall: SolidWallMass | SolidWall, subRoofs: Table<GeneratedRoof>) {
  // walls with non interior room enclosure are always considered exterior walls
  if (wallHasNonInteriorRoomEnclosure(wall)) {
    return true;
  }

  const {
    leftFace: { plane: roomLeftPlane },
    rightFace: { plane: roomRightPlane },
  } = wall.wallType === WallType.SOLID_MASS ? wall : wall.mass;

  const path = getWallPath(wall);
  const { startX, startY, endX, endY } = path;
  const [roomWallTangentX, roomWallTangentY] = line2Tangent(startX, startY, endX, endY);

  for (const subRoofId in subRoofs) {
    const { perimeters, exteriorWallOffsetsWithFinish } = subRoofs[subRoofId].roofVolume;
    const { wallPerimeter } = perimeters;
    for (let i = 0, l = polyLine2EdgeCount(wallPerimeter); i < l; ++i) {
      const [ax, ay] = polyLine2EdgeStart(wallPerimeter, i);
      const [bx, by] = polyLine2EdgeEnd(wallPerimeter, i);
      const [roofWallTangentX, roofWallTangentY] = polyLine2EdgeTangent(wallPerimeter, i);

      const roofRightPlane = planeByThreePoints(ax, ay, 0, bx, by, 0, bx, by, 1);
      const roofLeftPlane = planeByThreePoints(bx, by, 0, ax, ay, 0, ax, ay, 1);

      if (!roofRightPlane || !roofLeftPlane) {
        continue;
      }

      planeMoveAlongNormal(roofLeftPlane, exteriorWallOffsetsWithFinish[i]);

      // skip if they are not parallel and facing the same direction
      if (vec2Dot(roofWallTangentX, roofWallTangentY, roomWallTangentX, roomWallTangentY) < 1 - ANGLE_EPSILON) {
        continue;
      }

      // skip if they don't overlap in the tangent direction
      const [projectedStartX, projectedStartY] = line2ProjectPt(ax, ay, bx, by, startX, startY);
      const [projectedEndX, projectedEndY] = line2ProjectPt(ax, ay, bx, by, endX, endY);

      const intersection = intersectLine2Line2(
        ax,
        ay,
        bx,
        by,
        projectedStartX,
        projectedStartY,
        projectedEndX,
        projectedEndY,
      );
      if (!intersection || !(intersection instanceof CurveCurveRangeIntersection)) {
        continue;
      }

      // check for overlap in the normal direction
      // (using OPENING_TRANSFER_DISTANCE_TOLERANCE)
      if (
        planeSignedDistanceTo(roofLeftPlane, roomLeftPlane.originX, roomLeftPlane.originY, roomLeftPlane.originZ) <
          OPENING_TRANSFER_DISTANCE_TOLERANCE &&
        planeSignedDistanceTo(roofRightPlane, roomLeftPlane.originX, roomLeftPlane.originY, roomLeftPlane.originZ) <
          OPENING_TRANSFER_DISTANCE_TOLERANCE
      ) {
        return true;
      }

      if (
        planeSignedDistanceTo(roofLeftPlane, roomRightPlane.originX, roomRightPlane.originY, roomRightPlane.originZ) <
          OPENING_TRANSFER_DISTANCE_TOLERANCE &&
        planeSignedDistanceTo(roofRightPlane, roomRightPlane.originX, roomRightPlane.originY, roomRightPlane.originZ) <
          OPENING_TRANSFER_DISTANCE_TOLERANCE
      ) {
        return true;
      }
    }
  }

  return false;
}

function solidWallIsPartOfHalfStory(wall: SolidWall | SolidWallMass, blockIsHalfStory: { [blockId: string]: boolean }) {
  const path = getWallPath(wall);
  return (
    (path.leftRoom && blockIsHalfStory[(path.leftRoom as Room).parentId]) ||
    (path.rightRoom && blockIsHalfStory[(path.rightRoom as Room).parentId])
  );
}

function trimSimpleFaceWithBSP(face: Face, bsp: NodeBSP | null): boolean {
  const insidePolygons: PolygonBSP[] = [];

  const polygons = buildPolygonsFromFaces({ faces: [face] });
  polygons.forEach((x) =>
    splitPolygonBSP({ node: bsp, polygon: x, coplanarsToFront: false, backPolygons: insidePolygons }),
  );

  if (insidePolygons.length === 0) {
    return false;
  }

  const loops = insidePolygons.map((x) => {
    return offsetPolyLine2(
      polyLine2SetCW(planeProjectPolyLine3(face.plane, polyLine3Close(polyLine3New(x.vertices)))),
      -DIST_EPSILON,
    );
  });
  const loopUnion = polygonUnion(loops.map((x) => [x]));

  if (loopUnion.length === 0 || loopUnion[0].length === 0) {
    return false;
  }

  face.loops[0] = polyLine2SetCW(loopUnion[0][0]);

  return true;
}

export function buildRoofSystemHalfStoryDiscardBSPs(roofSystem: GeneratedRoofSystem): Table<NodeBSP> {
  const discardBSPs: RWTable<NodeBSP> = {};

  const { roofs: subRoofs } = roofSystem;
  for (const subRoofId in subRoofs) {
    const subRoof = subRoofs[subRoofId];

    const discardBSP = buildSubRoofHalfStoryDiscardBSP(subRoof);
    if (!discardBSP) {
      continue;
    }
    discardBSPs[subRoofId] = discardBSP;
  }

  return discardBSPs;
}

export function buildSubRoofHalfStoryDiscardBSP({ roofTopFaces }: GeneratedRoof): NodeBSP | null {
  let discardBSP: NodeBSP | null = null;

  const topPlane = planeNewXY(0, 0, 10000);
  for (let i = 0, l = roofTopFaces.length; i < l; i++) {
    const topFace = roofTopFaces[i].face;
    const { loops, plane } = topFace;

    if (loops.length === 0) {
      continue;
    }

    const discardHalfStoryWallPolygons: PolygonBSP[] = [];

    const copyOfFace = faceOrNullCopy(topFace);
    if (copyOfFace) {
      const faceReversed = faceOrNullReverse(copyOfFace);
      if (faceReversed) {
        buildPolygonsFromFaces({
          faces: [faceReversed],
          polygons: discardHalfStoryWallPolygons,
        });
      }
    }

    for (const loop of loops) {
      const pl3 = polyLine2OnPlane(plane, loop);
      for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
        const x1 = pl3[i + 3];
        const y1 = pl3[i + 4];
        const z1 = pl3[i + 5];

        const x2 = pl3[i];
        const y2 = pl3[i + 1];
        const z2 = pl3[i + 2];

        const x3 = x2;
        const y3 = y2;
        const z3 = topPlane.originZ;

        const x4 = x1;
        const y4 = y1;
        const z4 = topPlane.originZ;

        const edgePlane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

        if (!edgePlane) {
          continue;
        }

        discardHalfStoryWallPolygons.push(
          polygonBSPNew(0, [x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4], edgePlane),
        );
      }
    }

    discardBSP = addPolygonsToBSP(discardBSP, null, discardHalfStoryWallPolygons, MULTIGABLE_BSP_EPSILON);
  }

  return discardBSP;
}
